import React from "react";
import { Link } from "react-router-dom";
import SEO from "@components/SEO";
import { createBreadcrumbStructuredData } from "../utils/structuredData";
import { TruckIcon, MapPinIcon, UserIcon } from "@heroicons/react/24/outline";

const VehiclesPage: React.FC = () => {
  const vehicleTypes = [
    {
      title: "Rental Cars",
      description:
        "Comfortable and reliable cars perfect for cityadws driving, road trips and exploring New Zealand's scenic routes. Fuel efficient and well maintained vehicles with comprehensive insurance.",
      imageUrl: "/assets/ikhlas-tours-rental-cars.jpg",
      link: "/vehicles/cars",
      features: [
        "Comprehensive Insurance",
        "24/7 Roadside Assistance",
        "Ample Seating",
        "Air Conditioning",
        "NZ Road Rules Briefing",
      ],
    },
    {
      title: "Motorhomes",
      description:
        "Fully equipped motorhomes offering the freedom to explore New Zealand at your own pace. Perfect for adventure seekers who want comfort and flexibility on the road.",
      imageUrl: "/assets/motorhome-group-diagonal.jpg",
      link: "/vehicles/motorhomes",
      features: [
        "Self Contained Kitchen",
        "Sleeping for 2-6 People",
        "Shower & Toilet",
        "Power & Water Systems",
        "Campsite Ready",
      ],
    },
  ];

  const VehicleTypeCard: React.FC<{
    type: (typeof vehicleTypes)[0];
  }> = ({ type }) => (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group">
      <Link to={type.link} className="block">
        <div className="relative h-64 overflow-hidden">
          <img
            src={`${import.meta.env.VITE_CLOUDFRONT_URL}${type.imageUrl}`}
            alt={type.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-linear-to-t from-black/50 to-transparent" />
        </div>
      </Link>

      <div className="p-6">
        <h3 className="text-3xl font-bold text-primary mb-2">{type.title}</h3>
        <p className="text-gray-600 mb-6">{type.description}</p>

        {/* Features */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-primaryText mb-3">
            What's Included:
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {type.features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center text-sm text-gray-600"
              >
                <div className="w-1.5 h-1.5 bg-primary rounded-full mr-2 flex-shrink-0" />
                {feature}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <Link
          to={type.link}
          className="block w-full bg-primary text-white text-center py-3 px-4 rounded-md font-semibold hover:bg-secondary hover:text-primary transition-all duration-300"
        >
          Explore {type.title}
        </Link>
      </div>
    </div>
  );

  return (
    <div>
      <SEO
        title="New Zealand Car Rental & Motorhome Hire | Ikhlas Tours"
        description="New Zealand car rental and motorhome hire with comprehensive insurance and 24/7 roadside assistance. Self-drive your NZ adventure with full Muslim-friendly support."
        path="/vehicles"
        image="/assets/motorhome-group-diagonal.jpg"
        structuredData={createBreadcrumbStructuredData([
          { name: "Home", url: "/" },
          { name: "Vehicles", url: "/vehicles" },
        ])}
      />
      {/* Hero Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Vehicle Rentals
          </h1>
          <p className="text-xl text-primaryText max-w-3xl mx-auto">
            Choose from our selection of rental cars and motorhomes. Perfect for
            exploring New Zealand's stunning landscapes at your own pace.
          </p>
        </div>
      </section>

      {/* Vehicles Description */}
      {/* <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-700 leading-relaxed">
            Whether you prefer the freedom of a motorhome or the convenience of
            a rental car, we have the perfect vehicle for your New Zealand
            adventure. All our vehicles are well maintained, fully insured, and
            ready for your journey.
          </p>
        </div>
      </section> */}

      {/* Vehicle Types Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {vehicleTypes.map((type, index) => (
              <VehicleTypeCard key={index} type={type} />
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primaryText mb-4">
              What's included
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              All our vehicle rentals come with comprehensive benefits to ensure
              your peace of mind
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
            <div className="text-center">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <TruckIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Insurance Coverage
              </h3>
              <p className="text-gray-600 text-sm">
                Comprehensive insurance with excess reduction options
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <UserIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                24/7 Support
              </h3>
              <p className="text-gray-600 text-sm">
                Roadside assistance and customer support for any unforeseen
                events
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MapPinIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Flexible Pickup and Dropoff
              </h3>
              <p className="text-gray-600 text-sm">
                Multiple pickup and dropoff locations across New Zealand
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-primaryText mb-4">
            Ready to Start Your Adventure?
          </h2>
          <p className="text-xl text-primaryText mb-8">
            Contact our team to find the perfect vehicle for your New Zealand
            journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block bg-primary text-white px-8 py-3 rounded-md font-semibold hover:bg-opacity-90 transition-all duration-300"
            >
              Contact Us
            </Link>
            <button
              onClick={() =>
                window.open(
                  "https://wa.me/64221841601?text=Hi! I'm interested in vehicle rental.",
                  "_blank",
                )
              }
              className="inline-block bg-whatsapp text-white px-8 py-3 rounded-md font-semibold hover:bg-green-600 transition-all duration-300"
            >
              WhatsApp Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VehiclesPage;
